import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Wallet, Users, Briefcase, FileUp, FileDown, Repeat, Receipt,
  Settings, LogOut, ShieldAlert, Building2, Inbox, Mail, FilePieChart,
  Coins, ScrollText, UsersRound, FileSpreadsheet, TrendingUp, Network, Brain,
  PiggyBank, UserPlus, BadgePercent, SearchCheck, Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", roles: "*" },
  { to: "/forecast", label: "Forecast", icon: TrendingUp, testId: "nav-forecast", roles: "*" },
  { to: "/entscheidungen", label: "Entscheidungen", icon: Brain, testId: "nav-decisions", roles: "*" },
  { to: "/zahlungs-prioritaeten", label: "Zahlungs-Prioritäten", icon: BadgePercent, testId: "nav-priorities", roles: "*" },
  { to: "/kosten-audit", label: "Kosten-Audit", icon: SearchCheck, testId: "nav-audit-expenses", roles: "*" },
  { to: "/ruecklagen", label: "Rücklagen", icon: PiggyBank, testId: "nav-reserves", roles: "*" },
  { to: "/hiring", label: "Hiring", icon: UserPlus, testId: "nav-hiring", roles: ["owner", "admin", "accounting"] },
  { to: "/klumpenrisiko", label: "Klumpenrisiko", icon: Network, testId: "nav-concentration", roles: "*" },
  { to: "/inbox", label: "Inbox", icon: Inbox, testId: "nav-inbox", roles: "*" },
  { to: "/konten", label: "Konten", icon: Wallet, testId: "nav-accounts", roles: "*" },
  { to: "/kunden", label: "Kunden", icon: Users, testId: "nav-customers", roles: "*" },
  { to: "/projekte", label: "Projekte", icon: Briefcase, testId: "nav-projects", roles: "*" },
  { to: "/ausgangsrechnungen", label: "Ausgangsrechnungen", icon: FileUp, testId: "nav-outgoing", roles: "*" },
  { to: "/eingangsrechnungen", label: "Eingangsrechnungen", icon: FileDown, testId: "nav-incoming", roles: "*" },
  { to: "/zahlungsmodelle", label: "Zahlungsmodelle", icon: Coins, testId: "nav-payment-models", roles: ["owner", "admin", "accounting"] },
  { to: "/fixkosten", label: "Fixkosten", icon: Repeat, testId: "nav-fixed-costs", roles: "*" },
  { to: "/umsatzsteuer", label: "Umsatzsteuer", icon: Receipt, testId: "nav-vat", roles: "*" },
  { to: "/reports", label: "PDF-Reports", icon: FilePieChart, testId: "nav-reports", roles: "*" },
  { to: "/import-export", label: "Import / Export", icon: FileSpreadsheet, testId: "nav-import-export", roles: ["owner", "admin", "accounting"] },
  { to: "/warnungen", label: "Warnungen", icon: ShieldAlert, testId: "nav-alerts", roles: "*" },
  { to: "/imap", label: "IMAP-Konten", icon: Mail, testId: "nav-imap", roles: ["owner", "admin"] },
  { to: "/team", label: "Team", icon: UsersRound, testId: "nav-team", roles: ["owner", "admin"] },
  { to: "/audit", label: "Audit-Log", icon: ScrollText, testId: "nav-audit", roles: ["owner", "admin"] },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testId: "nav-settings", roles: ["owner", "admin"] },
];

export default function AppLayout({ children }) {
  const { user, tenant, logout, isPlatformAdmin } = useAuth();
  const location = useLocation();
  const userRole = user?.role || "team_readonly";
  const visibleNav = NAV.filter(n => n.roles === "*" || n.roles.includes(userRole));

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <ImpersonationBanner />
      <div className="flex flex-1">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-neutral-200 bg-white flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-neutral-200">
          <Link to="/" className="flex items-center gap-3" data-testid="tenant-selector">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-display font-bold text-sm"
              style={{ backgroundColor: "var(--tenant-primary)" }}
            >
              {tenant?.logo_url ? (
                <img src={tenant.logo_url} alt="Logo" className="h-9 w-9 rounded-lg object-cover" />
              ) : (
                <Building2 className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Mandant</div>
              <div className="text-sm font-display font-semibold text-neutral-900 truncate" data-testid="tenant-name">
                {tenant?.name || "—"}
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {visibleNav.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
          {isPlatformAdmin && (
            <>
              <div className="mt-4 mb-1 px-2 text-[10px] uppercase tracking-[0.22em] text-red-700 font-semibold">Platform</div>
              <NavLink
                to="/platform/tenants"
                data-testid="nav-platform-tenants"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Shield className="h-4 w-4" />
                <span>Alle Mandanten</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-neutral-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ backgroundColor: "var(--tenant-primary)" }}
            >
              {user?.name?.slice(0, 2)?.toUpperCase() || "AC"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-neutral-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-neutral-500 uppercase tracking-wider">{user?.role}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} title="Abmelden" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-x-hidden">
        <div className="px-8 py-6 max-w-[1400px] mx-auto" key={location.pathname}>
          {children}
        </div>
      </main>
      </div>
    </div>
  );
}
